import React, { useState } from 'react'
import { graphql, navigate, useStaticQuery, withPrefix } from 'gatsby'
import { Helmet } from 'react-helmet'
import {
  Box,
  Divider,
  GridView,
  HeadingUppercase,
  StackView,
  Icon,
} from '@planning-center/ui-kit'

import { getSortedCategories } from '../categories'
import { AppProvider } from '../components/AppProvider'
import PageHeader from '../components/PageHeader'

import '../css/prism-theme.css'

function isLinkActive(slug, pathname) {
  // account for trailing slash paths: /getting-started and /getting-started/
  slug = withPrefix(slug)
  return slug === pathname || slug === pathname.slice(0, -1)
}

function SidebarLink({ to, title, isActive }) {
  return (
    <StackView
      as="a"
      href={to}
      axis="horizontal"
      paddingHorizontal={2}
      paddingVertical={1.5}
      borderRightStyle="solid"
      borderRightWidth={3}
      borderRightColor={isActive ? 'primary' : 'transparent'}
      fontWeight={isActive ? 700 : 400}
      textDecoration="none"
      color={isActive ? 'primary' : 'foreground'}
      hover={{
        color: 'primary',
        backgroundColor: 'primary-lightest',
      }}
      className={isActive ? 'active' : ''}
      onClick={(event) => {
        event.preventDefault()
        navigate(to)
      }}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault()
          navigate(to)
        }
      }}
    >
      {title}
    </StackView>
  )
}

function SidebarGroup({ category, components, defaultOpen, location }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <StackView>
      <StackView
        as="button"
        axis="horizontal"
        alignment="center"
        distribution="space-between"
        padding={2}
        spacing={1}
        color={open ? 'foreground' : 'foregroundSecondary'}
        cursor="pointer"
        hover={{ color: 'foreground' }}
        onClick={() => setOpen((bool) => !bool)}
      >
        <HeadingUppercase color="inherit" cursor="inherit">
          {category}
        </HeadingUppercase>
        <Icon
          name="caret-up"
          color="foregroundTertiary"
          rotate={open ? 0 : 90}
        />
      </StackView>
      <StackView visible={open} paddingBottom={2}>
        {components
          .filter((component) => component.title !== 'Table')
          .map((component) => (
            <SidebarLink
              key={component.slug}
              to={component.slug}
              title={component.title}
              isActive={isLinkActive(component.slug, location.pathname)}
            />
          ))}
      </StackView>
    </StackView>
  )
}

function ComponentsLayout({ children, location, path }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const data = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
        }
        fields {
          version
        }
      }
      allMdx(filter: { fields: { slug: { ne: null } } }) {
        nodes {
          frontmatter {
            title
            category
            order
          }
          fields {
            slug
          }
        }
      }
    }
  `)
  const categories = getSortedCategories(
    data.allMdx.nodes.map((node) => ({
      title: node.frontmatter.title,
      category: node.frontmatter.category,
      order: node.frontmatter.order,
      slug: node.fields.slug,
    }))
  )
  return (
    <AppProvider>
      <Helmet title={data.site.siteMetadata.title} />
      <GridView
        width="100%"
        minHeight="100vh"
        rows="auto 1fr"
        mediaQueries={{
          md: {
            columns: [
              'minmax(224px, 272px)',
              'minmax(64px, 1fr)',
              'minmax(380px, 1056px)',
              'minmax(64px, 1fr)',
            ],
          },
        }}
      >
        <Box
          columnStart={1}
          columnEnd={-1}
          rowStart={1}
          rowEnd={1}
          css={{
            backgroundColor: 'hsl(200, 10%, 8%)',
          }}
        />
        <PageHeader
          version={data.site.fields.version}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
        />
        <Box
          as="aside"
          visible={menuOpen}
          columnStart={1}
          columnEnd={-1}
          mediaQueries={{
            md: {
              visible: true,
              columnStart: 1,
              columnEnd: 2,
              rowStart: 2,
            },
          }}
        >
          <StackView
            height={menuOpen ? undefined : '100vh'}
            top={0}
            overflow="auto"
            spacing={<Divider />}
            mediaQueries={{
              md: { position: 'sticky' },
            }}
          >
            {categories.map(({ category, components }) => (
              <SidebarGroup
                key={category}
                category={category}
                components={components}
                location={location}
                defaultOpen={components.some(
                  (component) => path === component.slug
                )}
              />
            ))}
            <Divider />
          </StackView>
        </Box>
        <Divider
          visible={false}
          axis="vertical"
          column={2}
          rowStart={2}
          mediaQueries={{
            md: {
              visible: true,
            },
          }}
        />
        {!menuOpen && (
          <StackView
            as="main"
            mediaQueries={{
              md: {
                columnStart: 3,
                columnEnd: 4,
                rowStart: 2,
                rowEnd: 2,
              },
            }}
            className="DocSearch-content"
          >
            {children}
          </StackView>
        )}
      </GridView>
    </AppProvider>
  )
}

export default ComponentsLayout
